import type { FC } from "react";
import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import Xarrow from "react-xarrows";

import type { Arrow } from "../../../types/main";
import { isDropzone } from "../../../utils/isDropzone";

type Props = {
    addArrow: (arrow: Arrow) => void;
    color?: string;
    column: number;
    dropzones: [string, number][];
    id: number;
    itemId: string;
    startRef: React.MutableRefObject<null>;
};

const ExerciseConformityPointFC: FC<Props> = ({ addArrow, color, column, dropzones, id, itemId, startRef }: Props) => {
    const endRef = useRef(null);

    const [position, setPosition] = useState({});
    const [beingDragged, setBeingDragged] = useState(false);
    const style = useMemo(
        () => ({
            ...{
                left: 0,
                top: 0,
            },
            ...position,
        }),
        [position]
    );
    const onDrag = useCallback(
        (e) =>
            setPosition({
                height: 0,
                left: e.clientX,
                opacity: 0,
                position: "fixed",
                top: e.clientY,
                transform: "none",
                width: 0,
            }),
        []
    );
    const onDragEnd = useCallback(() => {
        setPosition({});
        setBeingDragged(false);
    }, []);
    const onDragStart = useCallback(
        (e) => {
            setBeingDragged(true);
            e.dataTransfer.setData("column", column);
            e.dataTransfer.setData("id", id);
            e.dataTransfer.setData("itemId", itemId);
        },
        [column, id, itemId]
    );

    const onTouchMove = useCallback(
        (e) => {
            const { clientX, clientY } = e.touches[0];
            (window as any).dropzone = dropzones.find(([id]) => isDropzone(clientX, clientY, id));

            setPosition({
                height: 0,
                left: clientX,
                opacity: 0,
                position: "fixed",
                top: clientY,
                transform: "none",
                width: 0,
            });
        },
        [dropzones]
    );
    const onTouchEnd = useCallback(() => {
        const dropzone = (window as any).dropzone as [string, number] | undefined;
        if (dropzone) {
            addArrow({
                end: dropzone[0],
                endId: dropzone[1],
                start: itemId,
                startId: id,
            });
        }
        (window as any).dropzone = undefined;

        setPosition({});
        setBeingDragged(false);
    }, [addArrow, id, itemId]);
    const onTouchStart = useCallback(() => {
        setBeingDragged(true);
    }, []);

    return (
        <>
            <div
                className="exercise-conformity__connect-point"
                draggable
                onDrag={onDrag}
                onDragEnd={onDragEnd}
                onDragStart={onDragStart}
                onTouchEnd={onTouchEnd}
                onTouchMove={onTouchMove}
                onTouchStart={onTouchStart}
                ref={endRef}
                style={style}
            />
            {beingDragged && (
                <Xarrow color={color} dashness end={endRef} headSize={4} showHead start={startRef} strokeWidth={3} />
            )}
        </>
    );
};

export const ExerciseConformityPoint = memo(ExerciseConformityPointFC);
